import { useMemo } from 'react';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useServicePlanTemplateCtx } from '@/app/(app)/settings/service_plans/[id]/provider';
import { ButtonEmpty } from '@/components/atoms/button-empty';
import { Link } from '@/components/link';
import { ServiceTypeTemplate, useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEditableState } from '@/hooks/use-editable-state';

export function ServicePlanTemplateServiceTypes() {
  const { api } = useApiV1Ctx();
  const { servicePlanTemplate: initial, refetch } = useServicePlanTemplateCtx();

  // TODO: Add coverage input
  const state = useEditableState(initial, (template) =>
    template.service_type_templates.map((st) => st.service_type_template_id)
  );

  // Get all available service type templates
  const { serviceTypeTemplates: allServiceTypeTemplates } = useServiceTypeTemplatesCtx();

  const { mutate } = api.servicePlanTemplates.updateServicePlanTemplatesTemplateIdPatch.useMutation(
    {
      path: { template_id: initial.id },
    },
    {
      onSuccess: (data) => {
        toast.success('Service plan updated');
        state.reset(data);
        refetch();
      },
    }
  );

  const handleSave = () => {
    mutate({
      service_type_templates: state.value.map((id) => ({
        coverage: 'COVERED' as const,
        service_type_template_id: id,
      })),
    });
  };

  const addServiceType = (serviceTypeId: string) => {
    state.setValue((prev) => [...prev, serviceTypeId]);
  };

  const removeServiceType = (serviceTypeId: string) => {
    state.setValue((prev) => prev.filter((id) => id !== serviceTypeId));
  };

  const [currentServiceTypes, availableServiceTypes] = useMemo(() => {
    return [
      allServiceTypeTemplates.filter((st) => state.value.includes(st.id)),
      allServiceTypeTemplates.filter((st) => !state.value.includes(st.id)),
    ];
  }, [allServiceTypeTemplates, state.value]);

  return (
    <Card className='w-full max-w-4xl'>
      <CardHeader>
        <div className='flex items-center justify-between gap-2'>
          <div className='space-y-1.5'>
            <CardTitle>Service Types</CardTitle>
            <p className='text-sm text-muted-foreground'>
              Configure which service types are included in this service plan.
            </p>
          </div>
          <Button onClick={handleSave} disabled={!state.isDirty}>
            Save
          </Button>
        </div>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        {/* Add Service Type */}
        <div className='flex items-center gap-3'>
          <Select onValueChange={addServiceType}>
            <SelectTrigger className='flex-1'>
              <SelectValue placeholder='Select a service type to add' />
            </SelectTrigger>
            <SelectContent>
              {availableServiceTypes.map((serviceType) => (
                <SelectItem key={serviceType.id} value={serviceType.id}>
                  {serviceType.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant='outline'
            size='icon'
            disabled={availableServiceTypes.length === 0}
            onClick={() => {
              // This will be handled by the select onValueChange
            }}
          >
            <PlusIcon className='h-4 w-4' />
          </Button>
        </div>

        {/* Current Service Types */}
        <div>
          <h4 className='mb-4 text-base font-medium'>Included Service Types</h4>
          {currentServiceTypes.length === 0 ? (
            <ButtonEmpty
              title='No service types added.'
              subtitle='Add service types to define what services are included in this plan.'
              onClick={() => {}}
            />
          ) : (
            <div className='space-y-3'>
              {currentServiceTypes.map((serviceType) => (
                <ServiceTypeItem
                  key={serviceType.id}
                  serviceType={serviceType}
                  onRemove={() => removeServiceType(serviceType.id)}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface ServiceTypeItemProps {
  serviceType: ServiceTypeTemplate;
  onRemove: () => void;
}

// TODO: move this to reusable components folder
export function ServiceTypeItem({ serviceType, onRemove }: ServiceTypeItemProps) {
  return (
    <div className='flex items-center justify-between rounded-lg border p-4'>
      <div className='flex flex-col gap-1'>
        <Link href={`/settings/service_types/${serviceType.id}`} className='text-sm font-medium hover:underline'>
          {serviceType.name}
        </Link>
        {serviceType.description && <div className='text-sm text-muted-foreground'>{serviceType.description}</div>}
      </div>
      <Button variant='outline' size='icon' onClick={onRemove}>
        <TrashIcon className='h-4 w-4' />
      </Button>
    </div>
  );
}
